/**
 * Worker Security Service
 * Handles worker assignment validation and unauthorized access
 */

export class WorkerSecurityService {
  private static instance: WorkerSecurityService;
  private isUnauthorized = false;
  private redirectUrl = '/login';

  // Routes that don't require authentication - 401s on these should NOT trigger redirect
  private publicRoutes = [
    '/video/',      // Public video viewer
    '/embed/',      // Embedded video player
    '/share/',      // Shared slides
    '/login',       // Login page
    '/waitlist',    // Waitlist page
    '/signup-flow', // Signup flow
    '/agent-chat',  // Ivy chat (own auth flow)
  ];

  private constructor() {
    // Set up global error handler for worker mismatches
    this.setupGlobalErrorHandler();
  }

  /**
   * Check if current page is a public route (doesn't require auth)
   */
  private isOnPublicRoute(): boolean {
    const currentPath = window.location.pathname;
    return this.publicRoutes.some(route => currentPath.startsWith(route));
  }

  static getInstance(): WorkerSecurityService {
    if (!WorkerSecurityService.instance) {
      WorkerSecurityService.instance = new WorkerSecurityService();
    }
    return WorkerSecurityService.instance;
  }

  /**
   * Handle worker mismatch error
   */
  handleWorkerMismatch(assignedTo?: string, currentUser?: string) {
    console.error('🚫 WORKER SECURITY VIOLATION DETECTED');
    console.error(`Worker assigned to: ${assignedTo || 'unknown'}`);
    console.error(`Current user: ${currentUser || 'unknown'}`);

    // Set unauthorized flag
    this.isUnauthorized = true;

    // Clear all stored data
    this.clearAllStoredData();

    // Log security event
    this.logSecurityEvent('WORKER_MISMATCH', { assignedTo, currentUser });

    // Redirect immediately
    this.redirectToExternal();
  }

  /**
   * Handle unauthenticated access
   */
  handleUnauthenticated() {
    console.error('🚫 UNAUTHENTICATED ACCESS DETECTED — stack trace:', new Error().stack);

    // Set unauthorized flag
    this.isUnauthorized = true;

    // Clear all stored data
    this.clearAllStoredData();

    // Log security event
    this.logSecurityEvent('UNAUTHENTICATED', { timestamp: new Date().toISOString() });

    // Redirect immediately
    this.redirectToExternal();
  }

  /**
   * Clear all stored data for security
   */
  private clearAllStoredData() {
    // Clear all localStorage
    localStorage.clear();

    // Clear all sessionStorage
    sessionStorage.clear();

    // Clear any cookies (if accessible)
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
  }

  /**
   * Redirect to external site
   */
  private redirectToExternal() {
    // Use replace to prevent back button
    window.location.replace(this.redirectUrl);

    // Backup redirect methods in case first one fails
    setTimeout(() => {
      window.location.href = this.redirectUrl;
    }, 100);

    // Final fallback
    setTimeout(() => {
      window.open(this.redirectUrl, '_self');
    }, 200);
  }

  /**
   * Log security event for audit
   */
  private logSecurityEvent(event: string, data: any) {
    try {
      // Send security event to console (could be sent to server in production)
      console.warn(`[SECURITY EVENT] ${event}`, {
        timestamp: new Date().toISOString(),
        event,
        data,
        userAgent: navigator.userAgent,
        url: window.location.href
      });
    } catch (error) {
      console.error('Failed to log security event:', error);
    }
  }

  /**
   * Set up global error handler
   */
  private setupGlobalErrorHandler() {
    // Intercept all fetch requests — ONLY for WORKER_MISMATCH detection (403).
    // 401 handling is intentionally NOT done here because:
    // 1. The axios interceptor (api.ts) handles 401s for regular API calls with token refresh
    // 2. Different endpoints use different auth schemes (Firebase vs ivy-chat session tokens)
    //    — a global 401 handler can't know which token to refresh and causes false logouts
    // 3. handleUnauthenticated() calls localStorage.clear() which is a nuclear option
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);

        if (response.status === 403) {
          const clonedResponse = response.clone();
          try {
            const data = await clonedResponse.json();
            if (data.error === 'WORKER_MISMATCH') {
              this.handleWorkerMismatch(data.assignedTo, data.currentUser);
              throw new Error('WORKER_MISMATCH');
            }
          } catch (e) {
            // If parsing fails or it's a mismatch, continue
            if (e instanceof Error && e.message === 'WORKER_MISMATCH') {
              throw e;
            }
          }
        }

        return response;
      } catch (error) {
        throw error;
      }
    };

    // Also intercept XMLHttpRequest if used
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    const service = this;

    XMLHttpRequest.prototype.open = function (...args: any) {
      // @ts-ignore
      this._method = args[0];
      // @ts-ignore
      this._url = args[1];
      // @ts-ignore
      return originalOpen.apply(this, args);
    };

    XMLHttpRequest.prototype.send = function (...args: any) {
      this.addEventListener('load', function () {
        // 401 handling is NOT done here — axios has its own interceptor with
        // token refresh + retry logic. Handling 401 here would race with it
        // and nuke the session before axios can recover.
        if (this.status === 403) {
          try {
            const data = JSON.parse(this.responseText);
            if (data.error === 'WORKER_MISMATCH') {
              service.handleWorkerMismatch(data.assignedTo, data.currentUser);
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
      });
      // @ts-ignore
      return originalSend.apply(this, args);
    };
  }

  /**
   * Check if currently unauthorized
   */
  isCurrentlyUnauthorized(): boolean {
    return this.isUnauthorized;
  }

  /**
   * Validate worker access on app startup
   */
  async validateWorkerAccess(): Promise<boolean> {
    try {
      // Skip validation entirely on public routes - they don't require authentication
      if (this.isOnPublicRoute()) {
        return true;
      }

      // Check if we have a token
      const token = localStorage.getItem('bearer_token');
      if (!token) {
        console.warn('No authentication token found');
        return true; // Allow access if no token (will be handled by auth flow)
      }

      // Import api module dynamically to avoid circular dependency
      const { default: api } = await import('./api');

      // Probe a protected endpoint to detect a 403 WORKER_MISMATCH. Only the auth
      // result matters, so request a single row — fetching the full list here cost
      // ~1.4s on every load (thumbnail signing) and blocked the dashboard.
      try {
        await api.get('/conversations', { params: { limit: 1 } });
        return true;
      } catch (error: any) {
        if (error.response?.status === 403) {
          const data = error.response.data;
          if (data.error === 'WORKER_MISMATCH') {
            this.handleWorkerMismatch(data.assignedTo, this.extractUserFromToken(token));
            return false;
          }
        }
        throw error;
      }
    } catch (error) {
      console.error('Worker validation error:', error);
      // If it's a mismatch error, we've already handled it
      if (error instanceof Error && error.message === 'WORKER_MISMATCH') {
        return false;
      }
      return true; // Allow access on other errors
    }
  }

  /**
   * Extract user ID from JWT token
   */
  private extractUserFromToken(token: string): string | undefined {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId;
    } catch {
      return undefined;
    }
  }
}

// Export singleton instance
export const workerSecurityService = WorkerSecurityService.getInstance();